import React, { forwardRef } from "react";
import { ADAPTIVE_FLOW } from "./assessmentData";
import { formatActivityMode, OBSERVATION_QUESTIONS } from "./evidenceMapping";
import {
  EDUCATIONAL_CLAIM_BOUNDARY,
  getEducationalModules,
} from "./educationalModules";
import { HYDRATION_READINESS, RELEVANCE_COPY, RELEVANT_PRINCIPLES } from "./resultNarratives";
import {
  buildImplementationPlan,
  DEFAULT_IMPLEMENTATION_ANSWERS,
} from "./implementationPlan";

/**
 * Hidden print-only template for "Download My Adaptive Profile".
 * Rendered off-screen (fixed, negative left) at exact A4 width (794px @96dpi).
 * Uses inline styles to avoid Tailwind color-space issues during html2canvas.
 */

const brand = {
  ink: "#1B1F1F",
  soft: "#4A5352",
  mute: "#7A8483",
  faint: "#C6CCCB",
  canvas: "#FBF9F4",
  warm: "#F3EEE2",
  surface: "#FFFFFF",
  line: "#E9E4D8",
  lineSoft: "#EFEBE0",
  teal: "#3C7A73",
  tealDeep: "#26504B",
  tealSoft: "#DDECE9",
};

const Bar = ({ label, value }) => {
  const pct = Math.max(6, Math.min(100, (value / 5) * 100));
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.mute }}>
          {label}
        </span>
        <span style={{ fontSize: 12, color: brand.soft, fontFamily: "Georgia, serif" }}>
          {value.toFixed(1)} <span style={{ color: brand.faint }}>/ 5</span>
        </span>
      </div>
      <div style={{ height: 6, background: brand.line, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: brand.teal }} />
      </div>
    </div>
  );
};

const SectionTitle = ({ eyebrow, title }) => (
  <div style={{ marginTop: 28, marginBottom: 14 }}>
    <div
      style={{
        fontSize: 10,
        letterSpacing: "0.32em",
        textTransform: "uppercase",
        color: brand.tealDeep,
        marginBottom: 8,
      }}
    >
      {eyebrow}
    </div>
    <h2
      style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: 22,
        color: brand.ink,
        margin: 0,
        letterSpacing: "-0.01em",
        fontWeight: 500,
      }}
    >
      {title}
    </h2>
  </div>
);

const Card = ({ eyebrow, title, children, warm }) => (
  <div
    style={{
      background: warm ? brand.warm : brand.surface,
      border: `1px solid ${warm ? brand.lineSoft : brand.line}`,
      borderRadius: 14,
      padding: 18,
      marginBottom: 12,
    }}
  >
    {eyebrow && (
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: brand.tealDeep,
          marginBottom: 8,
        }}
      >
        {eyebrow}
      </div>
    )}
    {title && (
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 15,
          color: brand.ink,
          marginBottom: 10,
          fontWeight: 500,
        }}
      >
        {title}
      </div>
    )}
    {children}
  </div>
);

const Bullets = ({ items, arrow = "—", size = 12 }) => (
  <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
    {items.map((it, i) => (
      <li key={i} style={{ fontSize: size, color: brand.ink, marginBottom: 5, fontWeight: 300, lineHeight: 1.55 }}>
        {arrow} {it}
      </li>
    ))}
  </ul>
);

const PDF_PAGE_COUNT = 11;

const PdfPage = ({ children, pageNumber, title, cover = false }) => (
  <section
    data-pdf-page
    data-pdf-page-number={pageNumber}
    style={{
      width: 794,
      height: 1123,
      padding: "46px 54px 38px",
      boxSizing: "border-box",
      background: brand.canvas,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    {!cover && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 12,
          borderBottom: `1px solid ${brand.line}`,
          color: brand.mute,
          fontSize: 9.5,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <span>Adaptive State Interpretation Report</span>
        <span>{title}</span>
      </div>
    )}
    <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    <div
      style={{
        paddingTop: 12,
        borderTop: `1px solid ${brand.line}`,
        display: "flex",
        justifyContent: "space-between",
        color: brand.mute,
        fontSize: 9,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}
    >
      <span>FORR Health · Adaptive Physiology™</span>
      <span>{pageNumber} / {PDF_PAGE_COUNT}</span>
    </div>
  </section>
);

const FrameworkModuleGrid = ({ modules }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
    {modules.map((mod) => (
      <div
        key={mod.id}
        style={{
          background: brand.surface,
          border: `1px solid ${brand.line}`,
          borderRadius: 14,
          padding: 12,
        }}
      >
        <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: brand.ink, fontWeight: 500, marginBottom: 6 }}>
          {mod.title}
        </div>
        <div style={{ background: brand.warm, borderRadius: 9, padding: 7, fontSize: 9.25, color: brand.ink, lineHeight: 1.35, marginBottom: 7, fontWeight: 300 }}>
          <strong>Why this is in the result: </strong>{mod.scoreContext}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 7 }}>
          <div style={{ fontSize: 9.1, color: brand.soft, lineHeight: 1.38, fontWeight: 300 }}>
            <strong style={{ color: brand.tealDeep }}>Science: </strong>{mod.science}
          </div>
          <div style={{ fontSize: 9.1, color: brand.soft, lineHeight: 1.38, fontWeight: 300 }}>
            <strong style={{ color: brand.tealDeep }}>CNS & biology: </strong>{mod.biology}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 7 }}>
          <div style={{ fontSize: 9.1, color: brand.ink, lineHeight: 1.38, fontWeight: 300 }}>
            <strong>Profile application: </strong>{mod.profileRelevance}
          </div>
          <div style={{ fontSize: 9.1, color: brand.ink, lineHeight: 1.38, fontWeight: 300 }}>
            <strong>Homeostasis & recovery: </strong>{mod.homeostasis}
          </div>
        </div>
        <div style={{ background: brand.tealSoft, borderRadius: 9, padding: 7, fontSize: 9.25, color: brand.ink, lineHeight: 1.35, marginBottom: 6 }}>
          <strong>Why it influenced the plan: </strong>{mod.planLink}
        </div>
        <div style={{ fontSize: 7.8, color: brand.mute, lineHeight: 1.3 }}>
          Sources: {mod.sources.join("; ")}
        </div>
      </div>
    ))}
  </div>
);

const AssessmentPDF = forwardRef(function AssessmentPDF(
  { profile, focus, dimensions, mapping, plan, educationalModules, date },
  ref
) {
  const dateStr = new Date(date || Date.now()).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const m = mapping || {
    interpretation: "",
    possibleEmotional: [],
    possibleBehavioural: [],
    possibleBody: [],
    stateNeed: "",
    recommendedInput: {},
    frameworkExplanation: "",
    activities: [],
    professionalSummary: "",
    selfReportBasis: "",
    patternId: "",
  };
  const p =
    plan ||
    buildImplementationPlan({
      profile,
      mapping: m,
      answers: DEFAULT_IMPLEMENTATION_ANSWERS,
      now: new Date(date || Date.now()),
    });
  const modules =
    educationalModules ||
    getEducationalModules({
      profileKey: profile?.key,
      dimensions,
      answers: DEFAULT_IMPLEMENTATION_ANSWERS,
    });

  return (
    <div
      ref={ref}
      data-testid="assessment-pdf-template"
      style={{
        position: "fixed",
        left: -10000,
        top: 0,
        width: 794,
        background: brand.canvas,
        color: brand.ink,
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        padding: 0,
        boxSizing: "border-box",
      }}
    >
      <PdfPage pageNumber={1} title="Adaptive profile and loops" cover>
      {/* Masthead */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          paddingBottom: 20,
          borderBottom: `1px solid ${brand.line}`,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: brand.tealDeep,
              marginBottom: 6,
            }}
          >
            FORR Health · Adaptive Physiology™
          </div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: brand.ink }}>
            Adaptive State Interpretation Report
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: 11, color: brand.mute }}>
          <div>{dateStr}</div>
          <div style={{ marginTop: 2 }}>Adaptive profile</div>
        </div>
      </div>

      {/* Cover */}
      <div style={{ marginTop: 44, marginBottom: 28 }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: brand.tealDeep,
            marginBottom: 14,
          }}
        >
          Your Adaptive State Profile
        </div>
        <h1
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 42,
            color: brand.ink,
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
            fontWeight: 500,
            margin: 0,
            marginBottom: 12,
          }}
        >
          {profile.title}
        </h1>
        <div
          style={{
            fontFamily: "Georgia, serif",
            fontStyle: "italic",
            fontSize: 19,
            color: brand.tealDeep,
            marginBottom: 22,
          }}
        >
          {profile.subtitle}
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.7, color: brand.soft, margin: 0, maxWidth: 620, fontWeight: 300 }}>
          {profile.body}
        </p>
      </div>

      {/* Loop interpretation */}
      <SectionTitle
        eyebrow="Your Current Position in the Loops"
        title="Assess the state. Interrupt the pattern. Practise a different route."
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Card eyebrow="The Dependency Loop" title={p.loops.dependencyLabel}>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.soft, lineHeight: 1.6 }}>
            {p.loops.evidence}
          </p>
          <div style={{ marginTop: 10 }}>
            <strong style={{ fontSize: 11, color: brand.tealDeep }}>
              Best interruption point:{" "}
            </strong>
            <span style={{ fontSize: 11.5, color: brand.ink, lineHeight: 1.55 }}>
              {p.loops.interruptionPoint}
            </span>
          </div>
        </Card>
        <Card eyebrow="The Adaptive Loop" title={p.loops.adaptiveLabel} warm>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>
            {p.loops.route}
          </p>
          <p style={{ margin: "9px 0 0", fontSize: 11, color: brand.soft, lineHeight: 1.55 }}>
            {p.loops.rationale}
          </p>
        </Card>
      </div>

      </PdfPage>
      <PdfPage pageNumber={2} title="Current pattern">

      {/* Current pattern */}
      <SectionTitle eyebrow="Current Pattern" title="What your responses suggest" />
      <Card eyebrow="01 · Nervous System" title="Primary Nervous System State">
        <div style={{ fontFamily: "Georgia, serif", fontSize: 16, color: brand.ink, marginBottom: 8 }}>{m.stateName}</div>
        <p style={{ margin: 0, fontSize: 12, color: brand.soft, lineHeight: 1.65, fontWeight: 300 }}>
          {m.stateDescription}
        </p>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Card eyebrow="02 · Priorities" title="What Your Nervous System Is Prioritizing">
          <Bullets items={m.priorities} size={11} />
        </Card>
        <Card eyebrow="03 · Outputs" title={m.behaviouralTitle}>
          <Bullets items={m.behaviouralOutputs} size={11} />
        </Card>
        <Card eyebrow="04 · Recovery" title="Likely Recovery Challenges">
          <Bullets items={m.recoveryChallenges} size={11} />
        </Card>
      </div>
      <Card eyebrow="05 · Why This Matters" warm>
        <p style={{ margin: 0, fontSize: 12.5, color: brand.ink, lineHeight: 1.7, fontWeight: 300 }}>
          {m.whyThisMatters}
        </p>
      </Card>
      <Card eyebrow="How this may show up">
        <Bullets items={m.signs || []} size={11.5} />
      </Card>
      <Card eyebrow="The Bottom Line" warm>
        <p style={{ margin: "0 0 7px", fontSize: 11, color: brand.ink, lineHeight: 1.55 }}>{p.bottomLine.happening}</p>
        <p style={{ margin: "0 0 7px", fontSize: 11, color: brand.ink, lineHeight: 1.55 }}>{p.bottomLine.shameRelease}</p>
        <p style={{ margin: 0, fontSize: 11, color: brand.ink, lineHeight: 1.55 }}><strong>What to do next: </strong>{p.bottomLine.overallSuggestion}</p>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={3} title="Immediate and one-week plan">

      {/* Immediate implementation */}
      <SectionTitle eyebrow="Immediate and One-Week Implementation" title="Act now, place the daily anchor, then repeat the plan" />
      <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 12 }}>
        <div>
          <Card eyebrow="Today’s state and readiness" title={p.currentState.labels.join(" + ")}>
            <Bullets
              items={[
                `Emotional intensity: ${p.currentState.intensity} / 5`,
                `Available energy: ${p.currentState.energy} / 5`,
                `Readiness gate: ${p.readiness.title}`,
                p.readiness.reason,
              ]}
              size={10.5}
            />
          </Card>
          <Card eyebrow="What to do right now" title={p.immediate.title} warm>
            <div style={{ fontSize: 10.5, color: brand.tealDeep, marginBottom: 6 }}>{p.immediate.duration}</div>
            <p style={{ margin: "0 0 7px", fontSize: 10.5, color: brand.ink, lineHeight: 1.5 }}>{p.immediate.instruction}</p>
            <p style={{ margin: "0 0 7px", fontSize: 10.5, color: brand.soft, lineHeight: 1.5 }}><strong>Why it matches: </strong>{p.immediate.rationale}</p>
            <p style={{ margin: "0 0 7px", fontSize: 10.5, color: brand.soft, lineHeight: 1.5 }}><strong>Recovery: </strong>{p.immediate.recovery}</p>
            <p style={{ margin: 0, fontSize: 10.5, color: brand.ink, lineHeight: 1.5 }}><strong>Check afterward: </strong>{p.immediate.reassess}</p>
          </Card>
        </div>
        <div>
          <Card eyebrow="Daily regulation anchor" title={`${p.anchor.slotLabel} at ${p.anchor.time}`}>
            <Bullets
              items={[
                `Duration: ${p.anchor.durationMinutes} minutes`,
                `Matched input: ${p.anchor.activity}`,
                `Format: ${p.anchor.socialFormat}`,
                p.anchor.rationale,
              ]}
              size={10.5}
            />
          </Card>
          <Card eyebrow="Your one-week change" title={p.week.title} warm>
            <p style={{ margin: "0 0 7px", fontSize: 10.5, color: brand.ink, lineHeight: 1.5 }}>{p.week.target}</p>
            <Bullets
              items={[
                `Plan purpose: ${p.week.purpose}`,
                `Primary input: ${p.week.input}`,
                "Frequency: three clear prompts each day for seven days",
                `Relative intensity: ${p.week.intensity}`,
                `Deliberate recovery: ${p.week.recovery}`,
                `Accessible alternative: ${p.week.alternative}`,
              ]}
              size={10.25}
            />
            <p style={{ margin: "7px 0 0", fontSize: 10.25, color: brand.ink, lineHeight: 1.45 }}><strong>Progression rule: </strong>{p.week.progressionRule}</p>
            <p style={{ margin: "7px 0 0", fontSize: 10.25, color: brand.soft, lineHeight: 1.45 }}>{p.week.experiment}</p>
          </Card>
        </div>
      </div>
      <Card eyebrow="Your complete seven-day schedule">
        <div style={{ display: "grid", gridTemplateColumns: "1fr", rowGap: 4 }}>
          {p.week.dailyPlan.map((day) => (
            <div key={day.date} style={{ fontSize: 9.5, color: brand.ink, lineHeight: 1.35 }}>
              <strong>{day.day} · {day.date}</strong> — {day.sessions.map((session) => `${session.time} ${session.activity}${session.recommendationOptions?.length ? `: ${session.recommendationOptions.map((option) => option.displayText).join("; ")}. ${session.choiceInstruction}` : ""} (${session.durationMinutes} min)${session.progressionRole && session.progressionStage !== "scheduled" ? ` — ${session.progressionRole}` : ""}${session.demandNotice ? ` — ${session.demandNotice}` : ""}`).join(" · ")}
            </div>
          ))}
        </div>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={4} title="Dimensions and physiological map">

      {/* Dimension Read */}
      <SectionTitle eyebrow="Dimension Read" title="How the system is currently running" />
      <Card>
        <Bar label="Current State" value={dimensions.state} />
        <Bar label="Current Inputs" value={dimensions.inputs} />
        <Bar label="Physical Capacity" value={dimensions.capacity} />
        <Bar label="Behavior Regulation" value={focus.scores.behaviors} />
        <div style={{ fontSize: 11, color: brand.mute, marginTop: 8, fontStyle: "italic" }}>
          Higher scores indicate a wider, more regulated system.
        </div>
      </Card>
      <Card eyebrow="How the parts connect" warm>
        <p style={{ margin: 0, fontSize: 12, color: brand.ink, lineHeight: 1.65, fontWeight: 300 }}>
          {m.frameworkExplanation}
        </p>
      </Card>

      {/* Supporting physiological map */}
      <SectionTitle eyebrow="Supporting Physiological Map" title="What is happening inside the two loops" />
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {ADAPTIVE_FLOW.map((n, i) => {
            const active = profile.flowHighlight === n.id;
            return (
              <div
                key={n.id}
                style={{
                  background: active ? brand.tealSoft : brand.surface,
                  border: `1px solid ${active ? brand.teal : brand.lineSoft}`,
                  borderRadius: 10,
                  padding: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.24em",
                    color: brand.tealDeep,
                    fontWeight: 500,
                    minWidth: 22,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 13, color: brand.ink }}>{n.label}</div>
                  <div style={{ fontSize: 11, color: brand.soft, fontWeight: 300 }}>{n.subtitle}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={5} title="Recommended input">

      {/* Recommended input */}
      <SectionTitle eyebrow="Recommended Input" title={m.recommendedInput?.label} />
      <Card eyebrow="Hydration and readiness">
        <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{HYDRATION_READINESS}</p>
      </Card>
      <Card warm>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 5 }}>
              Relative intensity
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: brand.ink, marginBottom: 6 }}>
              {m.recommendedInput?.intensity}
            </div>
            <div style={{ fontSize: 11.5, color: brand.soft, lineHeight: 1.6, fontWeight: 300 }}>
              {m.input?.intensityDefinition}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 5 }}>
              Purpose
            </div>
            <div style={{ fontSize: 11.5, color: brand.ink, lineHeight: 1.6, fontWeight: 300 }}>
              {m.input?.nervousSystemPurpose}
            </div>
          </div>
        </div>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Card eyebrow="Ways to deliver this input">
          <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.input?.delivery}</p>
          <p style={{ margin: "8px 0 0", fontSize: 10.5, color: brand.tealDeep, lineHeight: 1.5 }}>If you do not know which method, resistance, intensity, or progression fits your ability, talk to our coach.</p>
        </Card>
        <Card eyebrow="Expected adaptations" warm>
          <Bullets items={m.input?.adaptations || []} size={11} />
        </Card>
      </div>
      <Card eyebrow="Deliberate recovery · the adaptation phase">
        <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.input?.recovery}</p>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={6} title="Activity options">

      {/* Activity options */}
      <SectionTitle eyebrow="Activity Options" title="Ways to deliver this input" />
      {m.activities.map((a, i) => (
        <div
          key={a.id}
          style={{
            background: brand.surface,
            border: `1px solid ${brand.line}`,
            borderRadius: 14,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "baseline" }}>
            <div style={{ fontSize: 10, color: brand.tealDeep, letterSpacing: "0.24em" }}>
              {String(i + 1).padStart(2, "0")}
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 13.5, color: brand.ink, fontWeight: 500 }}>
              {a.name}
            </div>
          </div>
          <p style={{ margin: "0 0 6px 0", fontSize: 10.75, color: brand.soft, lineHeight: 1.45, fontWeight: 300 }}>
            {a.whyItMayFit}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 4 }}>
                Intensity
              </div>
              <p style={{ margin: 0, fontSize: 10.5, color: brand.ink, fontWeight: 300, lineHeight: 1.4 }}>{a.intensity}</p>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 4 }}>
                Mode
              </div>
              <p style={{ margin: 0, fontSize: 10.5, color: brand.ink, fontWeight: 300, lineHeight: 1.4 }}>{formatActivityMode(a.mode)}</p>
            </div>
          </div>
          <div style={{ background: brand.warm, border: `1px solid ${brand.lineSoft}`, borderRadius: 10, padding: 8 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 4 }}>
              Examples
            </div>
            <p style={{ margin: 0, fontSize: 10.5, color: brand.ink, fontWeight: 300, lineHeight: 1.4 }}>{a.examples}</p>
          </div>
          <div style={{ marginTop: 6, background: brand.tealSoft, border: `1px solid ${brand.lineSoft}`, borderRadius: 10, padding: 8 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 4 }}>
              Expected nervous-system and physiological adaptations
            </div>
            <p style={{ margin: 0, fontSize: 10.5, color: brand.ink, fontWeight: 300, lineHeight: 1.4 }}>{a.benefits}</p>
          </div>
        </div>
      ))}

      </PdfPage>
      <PdfPage pageNumber={7} title="Adaptive Physiological Summary">

      {/* Adaptive Physiological Summary — mirrors the on-screen panel */}
      <SectionTitle
        eyebrow="Adaptive Physiological Summary"
        title="For the professionals supporting your health, recovery, or performance"
      />
      <Card>
        <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 6 }}>Central and autonomic nervous-system interpretation</div>
        <p style={{ margin: "0 0 10px", fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.professional?.cns}</p>
        <p style={{ margin: "0 0 14px", fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.professional?.interpretation}</p>
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: brand.tealDeep,
              marginBottom: 8,
            }}
          >
            Profile basis
          </div>
          <Bullets
            items={[
              `Reported pattern: ${m.selfReportBasis}`,
              `Current State score: ${dimensions.state.toFixed(2)} / 5`,
              `Current Inputs score: ${dimensions.inputs.toFixed(2)} / 5`,
              `Physical Capacity score: ${dimensions.capacity.toFixed(2)} / 5`,
              `Behavior-regulation score: ${focus.scores.behaviors.toFixed(2)} / 5`,
              `Matched pattern: ${m.patternId}`,
            ]}
            size={11.5}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 8 }}>
            Current-state implementation layer
          </div>
          <Bullets
            items={[
              `Current read: ${p.currentState.summary}`,
              `Readiness gate: ${p.readiness.title}`,
              `Immediate input: ${p.immediate.title}`,
              `Daily anchor: ${p.anchor.slotLabel} at ${p.anchor.time} for ${p.anchor.durationMinutes} minutes`,
            ]}
            size={11}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 6 }}>
            Dependency and Adaptive Loop interpretation
          </div>
          <p style={{ margin: "0 0 7px", fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>
            {p.loops.dependencySupported
              ? `The strongest supported Dependency Loop pressure is ${p.loops.dependencyLabel}. The selected interruption point is ${p.loops.interruptionPoint}`
              : p.loops.evidence}
          </p>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.soft, lineHeight: 1.6 }}>
            Adaptive route: {p.loops.route}. {p.loops.rationale}
          </p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 6 }}>How physiology can influence emotion and behaviour</div>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.professional?.mechanism}</p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 6 }}>Why this input matches the profile</div>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>{m.professional?.matchedInput}</p>
        </div>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={8} title="Expected response and evidence">
      <SectionTitle
        eyebrow="Adaptive Physiological Summary"
        title="Expected response, repeated adaptation, and evidence"
      />
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <Card eyebrow="Expected acute response" warm><p style={{ margin: 0, fontSize: 11, lineHeight: 1.55 }}>{m.professional?.acute}</p></Card>
          <Card eyebrow="Expected repeated adaptation" warm><p style={{ margin: 0, fontSize: 11, lineHeight: 1.55 }}>{m.professional?.repeated}</p></Card>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 6 }}>
            Seven-day implementation
          </div>
          <p style={{ margin: 0, fontSize: 11.5, color: brand.ink, lineHeight: 1.6 }}>
            {p.week.target} The plan schedules {p.week.frequency} session{p.week.frequency === 1 ? "" : "s"} using {p.anchor.activity}, with the following recovery instruction: {p.week.recovery}
          </p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: brand.tealDeep,
              marginBottom: 8,
            }}
          >
            Why these inputs help
          </div>
          {(m.activities || []).map((a) => (
            <div key={a.id} style={{ fontSize: 11.5, color: brand.ink, lineHeight: 1.55, fontWeight: 300, marginBottom: 10 }}>
              <span style={{ fontWeight: 500 }}>{a.name}:</span> {a.professional?.benefitSummary}
              <br />
              <span style={{ color: brand.mute, fontStyle: "italic" }}>
                Sources: {(a.professional?.sourceIds || []).join("; ")}
              </span>
            </div>
          ))}
        </div>
      </Card>

      </PdfPage>
      <PdfPage pageNumber={9} title="Monitoring and relevance">
      <SectionTitle
        eyebrow="Adaptive Physiological Summary"
        title="Monitoring, literature, and professional relevance"
      />
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 8 }}>Response monitoring</div>
          <Bullets items={OBSERVATION_QUESTIONS} size={11} />
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: brand.tealDeep, marginBottom: 8 }}>Profile-matched literature</div>
          <Bullets items={m.professional?.sources || []} size={10.5} />
        </div>
        </div>
      </Card>

      {/* Applications & Programs */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12, marginBottom: 12 }}>
        <Card eyebrow="Relevant Applications">
          {(profile.applications || []).map((a) => (
            <div key={a.to} style={{ marginBottom: 9 }}>
              <div style={{ fontSize: 12, color: brand.ink }}>→ {a.label}</div>
              <div style={{ fontSize: 10.5, color: brand.soft, lineHeight: 1.5, marginTop: 2 }}>{RELEVANCE_COPY[a.label]}</div>
            </div>
          ))}
        </Card>
        <Card eyebrow="Relevant Programs">
          {(profile.programs || []).map((a) => (
            <div key={a.to} style={{ marginBottom: 9 }}>
              <div style={{ fontSize: 12, color: brand.ink }}>→ {a.label}</div>
              <div style={{ fontSize: 10.5, color: brand.soft, lineHeight: 1.5, marginTop: 2 }}>{RELEVANCE_COPY[a.label]}</div>
            </div>
          ))}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "0.7fr 1.3fr", gap: 10, marginTop: 12 }}>
        <Card eyebrow="Practice Stage">
          <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: brand.ink }}>{profile.frameworkStep}</div>
          <div style={{ fontSize: 10, color: brand.soft, marginTop: 4, fontWeight: 300, lineHeight: 1.4 }}>
            Selected interruption: {p.loops.interruptionPoint}
          </div>
        </Card>
        <Card eyebrow="Relevant Principles" warm>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 13, color: brand.ink, marginBottom: 5 }}>{RELEVANT_PRINCIPLES.title}</div>
          {RELEVANT_PRINCIPLES.paragraphs.map((paragraph) => (
            <p key={paragraph} style={{ fontSize: 9.5, color: brand.soft, lineHeight: 1.4, margin: "0 0 4px" }}>{paragraph}</p>
          ))}
          <p style={{ fontSize: 9.5, color: brand.ink, lineHeight: 1.4, margin: "5px 0" }}><strong>The central idea: </strong>{RELEVANT_PRINCIPLES.centralIdea}</p>
          <p style={{ fontSize: 8.5, color: brand.mute, lineHeight: 1.35, margin: 0 }}>Sources: {RELEVANT_PRINCIPLES.sources.join("; ")}</p>
        </Card>
      </div>

      </PdfPage>
      <PdfPage pageNumber={10} title="Neuroplasticity and BDNF">
      <SectionTitle eyebrow="Physiology behind the read" title="Neuroplasticity and BDNF" />
      <p style={{ margin: "-3px 0 12px", fontSize: 9.5, color: brand.mute, lineHeight: 1.45 }}>
        {EDUCATIONAL_CLAIM_BOUNDARY}
      </p>
      <FrameworkModuleGrid modules={modules.slice(0, 2)} />
      </PdfPage>

      <PdfPage pageNumber={11} title="Exercise modes and competition">
      <SectionTitle eyebrow="Physiology behind the read" title="Exercise modes and competition" />
      <FrameworkModuleGrid modules={modules.slice(2)} />
      </PdfPage>
    </div>
  );
});

export default AssessmentPDF;
